/**
 * GameTutorial - First-time player onboarding overlay
 * 
 * Shows essential controls when entering the 3D game for the first time.
 * State is managed via localStorage initially, designed to be easily
 * migrated to user preferences (hasSeenGameTutorial) later.
 * 
 * Features:
 * - Desktop and mobile control guides
 * - "Don't show again" option
 * - Accessible from game menu later
 */

import { useState, useEffect } from 'react';
import { X, Gamepad2, Mouse, Hand, Zap, Info, ArrowUp, Crosshair, Mic } from 'lucide-react';

// Storage key for tutorial state
const TUTORIAL_STORAGE_KEY = 'midgard_hasSeenGameTutorial';

/**
 * Check if user has seen the tutorial
 */
export const hasSeenTutorial = () => {
  try {
    return localStorage.getItem(TUTORIAL_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Mark tutorial as seen
 */
export const markTutorialSeen = () => {
  try {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
  } catch {
    // localStorage unavailable, ignore
  }
};

/**
 * Reset tutorial state (for testing or menu "Show Controls" option)
 */
export const resetTutorial = () => {
  try {
    localStorage.removeItem(TUTORIAL_STORAGE_KEY);
  } catch {
    // localStorage unavailable, ignore
  }
};

/**
 * Main Tutorial Overlay Component
 */
export default function GameTutorial({ onClose, isMobile }) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleStartExploring = () => {
    if (dontShowAgain) {
      markTutorialSeen();
    }
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
      data-testid="game-tutorial-overlay"
    >
      <div className="w-full max-w-lg bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 rounded-2xl border border-amber-900/40 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-amber-900/40 to-amber-800/20 border-b border-amber-900/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Gamepad2 className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="font-heading text-xl text-amber-100">Game Controls</h2>
          </div>
          <button 
            onClick={handleStartExploring}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="close-tutorial-btn"
          >
            <X className="w-5 h-5 text-amber-200/70" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Controls based on device */}
          {isMobile ? (
            <MobileControls />
          ) : (
            <DesktopControls />
          )}

          {/* Interaction hint */}
          <div className="p-4 rounded-xl bg-amber-900/20 border border-amber-700/30">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-100/80">
                Approach doors, objects, and other interactive elements to reveal available actions.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-black/30 border-t border-amber-900/20 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Don't show again checkbox */}
          <label className="flex items-center gap-2 cursor-pointer group">
            <input 
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-amber-700 bg-transparent text-amber-500 focus:ring-amber-500/50"
              data-testid="dont-show-again-checkbox"
            />
            <span className="text-sm text-amber-200/60 group-hover:text-amber-200/80 transition-colors">
              Don't show again
            </span>
          </label>

          {/* Start button */}
          <button
            onClick={handleStartExploring}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 text-white font-semibold hover:from-amber-500 hover:to-amber-600 transition-all hover:scale-105 shadow-lg shadow-amber-900/30"
            data-testid="start-exploring-btn"
          >
            Start Exploring
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Desktop control layout
 */
function DesktopControls() {
  const controls = [
    { 
      key: 'WASD', 
      action: 'Move',
      icon: null,
    },
    { 
      key: 'SHIFT', 
      action: 'Run',
      icon: Zap,
    },
    { 
      key: 'SPACE', 
      action: 'Jump',
      icon: ArrowUp,
    },
    { 
      key: 'E', 
      action: 'Interact',
      icon: Hand,
    },
    { 
      key: 'Left-click', 
      action: 'Action/Attack',
      icon: Crosshair,
    },
    { 
      key: 'Right-click + drag', 
      action: 'Look around',
      icon: Mouse,
    },
    { 
      key: 'V (hold)', 
      action: 'Push to Talk',
      icon: Mic,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {controls.map(({ key, action, icon: Icon }) => (
        <div 
          key={key}
          className="flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-amber-900/20"
        >
          <div className="flex-1">
            <p className="text-amber-100 font-medium text-sm">{action}</p>
            <p className="text-amber-400/70 text-xs font-mono">{key}</p>
          </div>
          {Icon && <Icon className="w-5 h-5 text-amber-500/60" />}
        </div>
      ))}
    </div>
  );
}

/**
 * Mobile control layout
 */
function MobileControls() {
  const controls = [
    { 
      action: 'Move',
      description: 'Left joystick',
      icon: Gamepad2,
    },
    { 
      action: 'Look around',
      description: 'Drag right side of screen',
      icon: Mouse,
    },
    { 
      action: 'Run',
      description: 'Sprint button (⚡)',
      icon: Zap,
    },
    { 
      action: 'Jump',
      description: 'Jump button (↑)',
      icon: ArrowUp,
    },
    { 
      action: 'Action/Attack',
      description: 'Action button (◎)',
      icon: Crosshair,
    },
    { 
      action: 'Interact',
      description: 'Contextual button when near objects',
      icon: Hand,
    },
    { 
      action: 'Push to Talk',
      description: 'PTT button (🎤) - hold to speak',
      icon: Mic,
    },
  ];

  return (
    <div className="space-y-3">
      {controls.map(({ action, description, icon: Icon }) => (
        <div 
          key={action}
          className="flex items-center gap-4 p-3 rounded-xl bg-black/30 border border-amber-900/20"
        >
          <div className="p-2 rounded-lg bg-amber-500/20">
            <Icon className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-amber-100 font-medium text-sm">{action}</p>
            <p className="text-amber-200/50 text-xs">{description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Contextual hint component for first-time mechanics
 * Shows small hints that disappear after user performs action
 */
export function ContextualHint({ children, visible, position = 'bottom-center' }) {
  if (!visible) return null;

  const positionClasses = {
    'bottom-center': 'bottom-20 left-1/2 -translate-x-1/2',
    'top-center': 'top-20 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-20 right-8',
  };

  return (
    <div 
      className={`fixed ${positionClasses[position]} z-40 px-4 py-2 rounded-full bg-black/80 backdrop-blur-sm border border-amber-500/50 animate-pulse pointer-events-none`}
      data-testid="contextual-hint"
    >
      <p className="text-amber-100 text-sm font-medium">{children}</p>
    </div>
  );
}
